import React, { useCallback, useMemo } from "react";
import { GroupableWorkTimeGrid } from "./groupableWorkTimeGrid";
import {
    useWorkTimeReportData,
    WorkTimeReportData,
} from "../../../hooks/reporting/useWorkTimeReportData";
import { MainLayout } from "../../layout/mainLayout";
import { LoadingIndicator } from "../../framework/loadingIndicatorNew";
import { GroupingValue } from "../../../store/reportOptionsSlice";
import { useTranslation } from "react-i18next";
import { WorkTimeReportGridRow } from "../../../models/reporting/workTimeReport";
import { stringOrNumberSortComparator } from "../../framework/dataGrid/sortComparator";
import { Base } from "../../../framework/base";
import {
    getAllChildRows,
    GroupableDataGridColDef,
    GroupedRow,
    GroupToggle,
} from "../../framework/groupedDataGrid/groupableDataGrid";
import {
    GridRenderCellParams,
    GridValueFormatterParams,
    GridValueGetterParams,
} from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { formatTime } from "../../salaries/salariesListGrid";
import { AppUtils } from "../../../models/common/appUtils";
import Alert from "@mui/material/Alert";
import {
    workTimeReportSelectableOtherCategories,
    workTimeReportSelectablePaidCategories,
    workTimeReportSelectableUnpaidCategories,
} from "../../../models/workTime/hourBookingCategory";
import { GroupableWorkTimeTopComponent } from "./groupableWorkTimeTopComponent";
import {
    DisplayMode,
    ReportTemplateType,
    SortModel,
} from "../../../models/reporting/reportTemplate";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import {
    setReportSortModel,
    setReportTemplateColumnOrder,
} from "../../../store/reportTemplateSlice";
import { ExcelFormatterResult } from "../../framework/groupedDataGrid/excelJsProcessing";

const GroupingColumn: { [key: string]: keyof WorkTimeReportGridRow } = {
    [GroupingValue.CostCenter]: "costCenterName",
    [GroupingValue.Customer]: "customerName",
    [GroupingValue.Employee]: "employeeName",
    [GroupingValue.Date]: "dateStr",
    [GroupingValue.Vehicle]: "vehicleName",
    [GroupingValue.WorkTimeType]: "workTimeTypeName",
    [GroupingValue.WorkShift]: "workShiftId",
};

const dataInitialized = (data: WorkTimeReportData) =>
    !!data.workTimeReportData &&
    !!data.salaryRowTypes &&
    !!data.employees &&
    !!data.employeeGroups;

interface Props {
    type: ReportTemplateType;
}

export const GroupableWorkTimeReportMain = ({ type }: Props) => {
    const data = useWorkTimeReportData();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const current = useAppSelector((state) => state.reportTemplate.current);

    const vehiclesByName = useAppSelector(
        (state) => state.reportOptions.vehicle.itemsByName
    );
    const workTimeTypesByName = useAppSelector(
        (state) => state.reportOptions.workTimeType.itemsByName
    );
    const customersByName = useAppSelector(
        (state) => state.reportOptions.customer.itemsByName
    );
    const costCenters = useAppSelector(
        (state) => state.reportOptions.costCenterOptions
    );
    const initialized =
        dataInitialized(data) &&
        !!vehiclesByName &&
        !!workTimeTypesByName &&
        !!customersByName &&
        !!costCenters;

    const {
        grouping,
        groupByRow,
        groupingModeMultiple: workTimeGroupingModeMultiple,
        showGroupedRows,
        selectedColumns,
        columnOrder,
        sortModel,
        display: { mode: displayMode, decimals },
    } = current;

    const onColumnOrderChange = useCallback(
        (val: string[]) => dispatch(setReportTemplateColumnOrder(val)),
        [dispatch]
    );

    const onSortModelChange = useCallback(
        (val: SortModel[]) => {
            dispatch(setReportSortModel(val));
        },
        [dispatch]
    );

    const groupingModeMultiple = useMemo(
        () => workTimeGroupingModeMultiple || !showGroupedRows,
        [workTimeGroupingModeMultiple, showGroupedRows]
    );

    const groupingModel = useMemo(
        () =>
            [
                ...grouping.map(
                    (g) =>
                        GroupingColumn[
                            g
                        ] as unknown as keyof WorkTimeReportGridRow
                ),
                (groupByRow
                    ? "id"
                    : null) as unknown as keyof WorkTimeReportGridRow,
            ].filter(Boolean),
        [grouping, groupByRow]
    );

    const { salaryRowTypes, employees } = data;

    const numberValueFormatter = useCallback(
        (params: GridValueFormatterParams<number>, isTimeUnit = true) => {
            const value = params.value;
            if (!value) return "-";

            if (isTimeUnit && displayMode === DisplayMode.Hours) {
                return AppUtils.getDurationStrByDurationMinShort(value);
            }

            const divisor = isTimeUnit ? 60 : 1;

            return (value / divisor).toLocaleFixed(decimals, "fi-Fi");
        },
        [displayMode, decimals]
    );

    const numberExcelFormatter = useCallback(
        (value: number, isTimeUnit = true): ExcelFormatterResult => {
            if (isTimeUnit && displayMode === DisplayMode.Hours) {
                return {
                    value: AppUtils.getDurationStrByDurationMinShort(
                        value,
                        true
                    ),
                    type: "string",
                };
            }

            const divisor = isTimeUnit ? 60 : 1;

            return { value: value / divisor, type: "number", decimals };
        },
        [displayMode, decimals]
    );

    const gridDef: GroupableDataGridColDef<WorkTimeReportGridRow>[] = useMemo(
        () => [
            {
                field: "duration",
                headerName: t("reporting.duration"),
                description: t("reporting.duration"),
                groupable: false,
                type: "number",
                minWidth: 100,
                flex: 1,
                valueGetter(params) {
                    return params.row?.totals?.duration ?? 0;
                },
                valueFormatter: numberValueFormatter,
                excelFormatter: numberExcelFormatter,
            },
            {
                field: "employeeName",
                headerName: t("reporting.employee"),
                description: t("reporting.employee"),
                groupable: true,
                type: "string",
                minWidth: 100,
                flex: 1,
                sortComparator: stringOrNumberSortComparator,
            },
            {
                field: "vehicleName",
                headerName: t("reporting.fleet"),
                description: t("reporting.fleet"),
                groupable: true,
                type: "string",
                minWidth: 100,
                flex: 1,
                sortComparator: stringOrNumberSortComparator,
            },
            {
                field: "costCenterName",
                headerName: t("reporting.costCenter"),
                description: t("reporting.costCenter"),
                groupable: true,
                type: "string",
                minWidth: 100,
                flex: 1,
                sortComparator: stringOrNumberSortComparator,
            },
            {
                field: "workTimeTypeName",
                headerName: t("reporting.workTimeType"),
                description: t("reporting.workTimeType"),
                groupable: true,
                type: "string",
                minWidth: 100,
                flex: 1,
                sortComparator: stringOrNumberSortComparator,
            },
            {
                field: "customerName",
                headerName: t("reporting.customer"),
                description: t("reporting.customer"),
                groupable: true,
                type: "string",
                minWidth: 100,
                flex: 1,
                sortComparator: stringOrNumberSortComparator,
            },
            {
                field: "id",
                groupable: true,
                type: "string",
                hideable: false,
                valueGetter: ({
                    row: { id, startTime, endTime, dateStr },
                }: GridValueGetterParams<
                    WorkTimeReportGridRow,
                    Partial<WorkTimeReportGridRow>
                >) => ({
                    id,
                    startTime,
                    endTime,
                    dateStr,
                }),
                sortComparator: (
                    v1: Partial<WorkTimeReportGridRow>,
                    v2: Partial<WorkTimeReportGridRow>
                ) => Base.stringCompare(v1.startTime, v2.endTime),
                valueFormatter: ({
                    value,
                }: GridValueFormatterParams<Partial<WorkTimeReportGridRow>>) =>
                    !!value?.startTime
                        ? `${formatTime(
                              value.startTime,
                              value.dateStr
                          )} - ${formatTime(value.endTime, value.dateStr)}`
                        : "-",
            },
            {
                field: "warning",
                groupable: false,
                hideable: false,
                type: "string",
                valueGetter: ({
                    rowNode,
                    api,
                }: GridValueGetterParams<WorkTimeReportGridRow, string>) =>
                    getAllChildRows<WorkTimeReportGridRow>(
                        rowNode,
                        api as GridApiPremium
                    ).some((r) => !r.isUpToDate)
                        ? t("reporting.notUpToDate").toString()
                        : null,
            },
            {
                field: "workShiftId",
                groupable: true,
                type: "string",
                hideable: false,
                valueGetter: ({
                    row: { id, workShiftStartTime, workShiftEndTime, dateStr },
                }: GridValueGetterParams<
                    WorkTimeReportGridRow,
                    Partial<WorkTimeReportGridRow>
                >) => ({
                    id,
                    workShiftStartTime,
                    workShiftEndTime,
                    dateStr,
                }),
                sortComparator: (
                    v1: Partial<WorkTimeReportGridRow>,
                    v2: Partial<WorkTimeReportGridRow>
                ) =>
                    Base.stringCompare(
                        v1.workShiftStartTime,
                        v2.workShiftEndTime
                    ),
                valueFormatter: ({
                    value,
                }: GridValueFormatterParams<Partial<WorkTimeReportGridRow>>) =>
                    [
                        !!value.dateStr
                            ? Base.dayjsToDateStrWithWeekday(value.dateStr)
                            : null,
                        !!value?.workShiftStartTime
                            ? `${formatTime(
                                  value.workShiftStartTime,
                                  value.dateStr
                              )} - ${formatTime(
                                  value.workShiftEndTime,
                                  value.dateStr
                              )}`
                            : "-",
                    ]
                        .filter(Boolean)
                        .join(" "),
            },
            {
                field: "dateStr",
                headerName: t("reporting.date"),
                description: t("reporting.date"),
                groupable: true,
                type: "string",
                minWidth: 100,
                flex: 1,
                valueGetter: ({
                    rowNode,
                    api,
                    value,
                }: GridValueGetterParams<WorkTimeReportGridRow, string>) => {
                    if (rowNode.type === "group") {
                        const childRows =
                            getAllChildRows<WorkTimeReportGridRow>(
                                rowNode,
                                api as GridApiPremium
                            );

                        const dates = Base.getUniqueStringItems(
                            childRows.map((r) => r.dateStr).filter(Boolean)
                        );

                        if (dates.length === 1) {
                            return dates[0];
                        }
                    }

                    return value;
                },
                renderCell: (
                    params: GridRenderCellParams<WorkTimeReportGridRow, string>
                ) =>
                    groupingModeMultiple && params.rowNode?.type === "group" ? (
                        <GroupToggle
                            params={
                                params as unknown as GridRenderCellParams<
                                    GroupedRow<WorkTimeReportGridRow>
                                >
                            }
                            label={
                                !!params.value
                                    ? Base.dayjsToDateStrWithWeekday(
                                        params.value
                                    )
                                    : ""
                            }
                            groupingField="workShiftId"
                        />
                    ) : !!params.value ? (
                        Base.dayjsToDateStrWithWeekday(params.value)
                    ) : (
                        "-"
                    ),
            },
            {
                field: "startTime",
                headerName: t("common.startTime"),
                description: t("common.startTime"),
                groupable: false,
                type: "string",
                minWidth: 100,
                flex: 1,
                renderCell: ({
                    value,
                    row,
                    rowNode,
                }: GridRenderCellParams<WorkTimeReportGridRow, string>) =>
                    rowNode.type === "group"
                        ? ""
                        : !!value
                            ? formatTime(value, row.dateStr)
                            : "-",
            },
            {
                field: "endTime",
                headerName: t("common.endTime"),
                description: t("common.endTime"),
                groupable: false,
                type: "string",
                minWidth: 100,
                flex: 1,
                renderCell: ({
                    value,
                    row,
                    rowNode,
                }: GridRenderCellParams<WorkTimeReportGridRow, string>) =>
                    rowNode.type === "group"
                        ? ""
                        : !!value
                            ? formatTime(value, row.dateStr)
                            : "-",
            },
            {
                field: "comment",
                headerName: t("reporting.comment"),
                description: t("reporting.comment"),
                groupable: false,
                type: "string",
                minWidth: 100,
                flex: 1,
                sortComparator: stringOrNumberSortComparator,
                valueGetter: ({
                    rowNode,
                    api,
                }: GridValueGetterParams<WorkTimeReportGridRow, string>) =>
                    Base.getUniqueStringItems(
                        getAllChildRows<WorkTimeReportGridRow>(
                            rowNode,
                            api as GridApiPremium
                        )
                            .map((r) => r.comment)
                            .filter(Boolean)
                    ).join(", "),
            },
            {
                field: "workShiftStartTime",
                headerName: t("reporting.workShiftStartTime"),
                description: t("reporting.workShiftStartTime"),
                groupable: false,
                type: "string",
                minWidth: 100,
                flex: 1,
                valueGetter: ({
                    rowNode,
                    api,
                    value,
                }: GridValueGetterParams<WorkTimeReportGridRow, string>) => {
                    if (rowNode.type === "group") {
                        const childRows =
                            getAllChildRows<WorkTimeReportGridRow>(
                                rowNode,
                                api as GridApiPremium
                            );

                        const workShiftIds = Base.getUniqueStringItems(
                            childRows.map((r) => r.workShiftId).filter(Boolean)
                        );

                        if (workShiftIds.length === 1) {
                            return childRows.find(
                                (r) => r.workShiftId === workShiftIds[0]
                            )?.workShiftStartTime;
                        }
                    }

                    return value;
                },
                renderCell: ({
                    value,
                    rowNode,
                }: GridRenderCellParams<WorkTimeReportGridRow, string>) =>
                    !!value
                        ? formatTime(value, value)
                        : rowNode.type === "group"
                            ? ""
                            : "-",
            },
            {
                field: "workShiftEndTime",
                headerName: t("reporting.workShiftEndTime"),
                description: t("reporting.workShiftEndTime"),
                groupable: false,
                type: "string",
                minWidth: 100,
                flex: 1,
                valueGetter: ({
                    rowNode,
                    api,
                    value,
                }: GridValueGetterParams<WorkTimeReportGridRow, string>) => {
                    if (rowNode.type === "group") {
                        const childRows =
                            getAllChildRows<WorkTimeReportGridRow>(
                                rowNode,
                                api as GridApiPremium
                            );

                        const workShiftIds = Base.getUniqueStringItems(
                            childRows.map((r) => r.workShiftId).filter(Boolean)
                        );

                        if (workShiftIds.length === 1) {
                            return childRows.find(
                                (r) => r.workShiftId === workShiftIds[0]
                            )?.workShiftEndTime;
                        }
                    }

                    return value;
                },
                renderCell: ({
                    value,
                    rowNode,
                }: GridRenderCellParams<WorkTimeReportGridRow, string>) =>
                    !!value
                        ? formatTime(value, value)
                        : rowNode.type === "group"
                            ? ""
                            : "-",
            },
            ...(salaryRowTypes ?? []).map(
                (srt): GroupableDataGridColDef<WorkTimeReportGridRow> => ({
                    field: `srt_${srt.id}`,
                    headerName: `${srt.name} (${srt.measureUnit})`,
                    description: `${srt.name} (${srt.measureUnit})`,
                    groupable: false,
                    type: "number",
                    minWidth: 100,
                    flex: 1,
                    valueGetter(params) {
                        return params.row?.totals?.[`srt_${srt.id}`] ?? 0;
                    },
                    valueFormatter: (
                        params: GridValueFormatterParams<number>
                    ) =>
                        numberValueFormatter(
                            params,
                            AppUtils.isTimeUnit(srt.measureUnit)
                        ),
                    excelFormatter: (value: number) =>
                        numberExcelFormatter(
                            value,
                            AppUtils.isTimeUnit(srt.measureUnit)
                        ),
                })
            ),
            ...workTimeReportSelectablePaidCategories().map(
                (c): GroupableDataGridColDef<WorkTimeReportGridRow> => ({
                    field: `cat_${c}`,
                    headerName: t(`hourBookingCategory.${c}`),
                    groupable: false,
                    type: "number",
                    minWidth: 100,
                    flex: 1,
                    valueGetter(params) {
                        return params.row?.totals?.[`cat_${c}`] ?? 0;
                    },
                    valueFormatter: numberValueFormatter,
                    excelFormatter: numberExcelFormatter,
                })
            ),
            ...workTimeReportSelectableUnpaidCategories().map(
                (c): GroupableDataGridColDef<WorkTimeReportGridRow> => ({
                    field: `cat_${c}`,
                    headerName: t(`hourBookingCategory.${c}`),
                    groupable: false,
                    type: "number",
                    minWidth: 100,
                    flex: 1,
                    headerClassName: "gray",
                    cellClassName: "gray",
                    valueGetter(params) {
                        return params.row?.totals?.[`cat_${c}`] ?? 0;
                    },
                    valueFormatter: numberValueFormatter,
                    excelFormatter: numberExcelFormatter,
                })
            ),
            ...workTimeReportSelectableOtherCategories().map(
                (c): GroupableDataGridColDef<WorkTimeReportGridRow> => ({
                    field: `cat_${c}`,
                    headerName: t(`hourBookingCategory.${c}`),
                    groupable: false,
                    type: "number",
                    minWidth: 100,
                    flex: 1,
                    valueGetter(params) {
                        return params.row?.totals?.[`cat_${c}`] ?? 0;
                    },
                    valueFormatter: numberValueFormatter,
                    excelFormatter: numberExcelFormatter,
                })
            ),
            ...Object.keys(workTimeTypesByName ?? {}).map(
                (key): GroupableDataGridColDef<WorkTimeReportGridRow> => {
                    const wtt = workTimeTypesByName[key][0];
                    const field = `wtt_${!wtt?.workTimeTypeId ? "null" : key}`;

                    return {
                        field,
                        headerName: key,
                        description: key,
                        groupable: false,
                        type: "number",
                        minWidth: 100,
                        flex: 1,
                        valueGetter(params) {
                            return params.row?.totals?.[field] ?? 0;
                        },
                        valueFormatter: numberValueFormatter,
                        excelFormatter: numberExcelFormatter,
                    };
                }
            ),
            ...(employees ?? []).map(
                (e): GroupableDataGridColDef<WorkTimeReportGridRow> => ({
                    field: `e_${e.id ?? "null"}`,
                    headerName: e.fullName,
                    description: e.fullName,
                    groupable: false,
                    type: "number",
                    minWidth: 100,
                    flex: 1,
                    valueGetter(params) {
                        return params.row?.totals?.[`e_${e.id ?? "null"}`] ?? 0;
                    },
                    valueFormatter: numberValueFormatter,
                    excelFormatter: numberExcelFormatter,
                })
            ),
            ...Object.keys(customersByName ?? {}).map(
                (key): GroupableDataGridColDef<WorkTimeReportGridRow> => {
                    const customer = customersByName[key][0];
                    const field = `c_${!customer?.id ? "null" : key}`;

                    return {
                        field,
                        headerName: key,
                        description: key,
                        groupable: false,
                        type: "number",
                        minWidth: 100,
                        flex: 1,
                        valueGetter(params) {
                            return params.row?.totals?.[field] ?? 0;
                        },
                        valueFormatter: numberValueFormatter,
                        excelFormatter: numberExcelFormatter,
                    };
                }
            ),
            ...(costCenters ?? []).map(
                (c): GroupableDataGridColDef<WorkTimeReportGridRow> => ({
                    field: `cc_${c.id ?? "null"}`,
                    headerName: c.title,
                    description: c.title,
                    groupable: false,
                    type: "number",
                    minWidth: 100,
                    flex: 1,
                    valueGetter(params) {
                        return (
                            params.row?.totals?.[`cc_${c.id ?? "null"}`] ?? 0
                        );
                    },
                    valueFormatter: numberValueFormatter,
                    excelFormatter: numberExcelFormatter,
                })
            ),
            ...Object.keys(vehiclesByName ?? {}).map(
                (key): GroupableDataGridColDef<WorkTimeReportGridRow> => {
                    const vehicle = vehiclesByName[key][0];
                    const field = `v_${!vehicle?.id ? "null" : key}`;

                    return {
                        field,
                        headerName: key,
                        description: [key, vehicle?.brand]
                            .filter(Boolean)
                            .join(" - "),
                        groupable: false,
                        type: "number",
                        minWidth: 100,
                        flex: 1,
                        valueGetter(params) {
                            return params.row?.totals?.[field] ?? 0;
                        },
                        valueFormatter: numberValueFormatter,
                        excelFormatter: numberExcelFormatter,
                    };
                }
            ),
        ],
        [
            salaryRowTypes,
            workTimeTypesByName,
            employees,
            customersByName,
            costCenters,
            vehiclesByName,
            groupingModel,
            groupingModeMultiple,
            t,
            numberValueFormatter,
            numberExcelFormatter,
        ]
    );

    const pinnedColumns = useMemo(
        () => [
            ...(groupingModeMultiple && grouping.includes("workShiftId")
                ? ["dateStr", "workShiftStartTime", "workShiftEndTime"]
                : []),
            ...(groupingModeMultiple &&
            !grouping.includes("workShiftId") &&
            groupByRow
                ? ["dateStr"]
                : []),
            ...(groupingModeMultiple && groupByRow
                ? ["startTime", "endTime"]
                : []),
        ],
        [grouping, groupByRow, groupingModeMultiple]
    );

    const columns = useMemo(
        () =>
            gridDef
                // Sort columns to maintain correct order
                .sort(
                    (a, b) =>
                        columnOrder.findIndex((col) => col === a.field) -
                        columnOrder.findIndex((col) => col === b.field)
                ),
        [gridDef, selectedColumns, columnOrder]
    );

    return (
        <MainLayout
            noContainer
            noTopContainer
            topComponent={
                <GroupableWorkTimeTopComponent
                    employees={employees}
                    employeeGroups={data.employeeGroups}
                    salaryRowTypes={salaryRowTypes}
                    salaryPeriodTypes={data.salaryPeriodTypes}
                    employeeGroupCalculations={data.employeeGroupCalculations}
                    reload={data.reload}
                    initialized={initialized}
                    grouping={grouping}
                    groupingModel={groupingModel}
                    groupByRow={groupByRow}
                    groupingModeMultiple={groupingModeMultiple}
                    showGroupedRows={showGroupedRows}
                    selectedColumns={selectedColumns}
                    pinnedColumns={pinnedColumns}
                    gridDef={gridDef}
                    type={type}
                    displayMode={displayMode}
                    decimals={decimals}
                />
            }
        >
            <>
                <LoadingIndicator loading={data.loading || !initialized} />
                {initialized &&
                data.employeeGroupCalculations?.length > 0 &&
                data.workTimeReportData?.length > 0 ? (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        {t("reporting.missingWorkTimeData")}
                    </Alert>
                    ) : null}
                {initialized && (
                    <GroupableWorkTimeGrid
                        columns={columns}
                        groupingModel={groupingModel}
                        pinnedColumns={pinnedColumns}
                        grouping={grouping}
                        groupingModeMultiple={groupingModeMultiple}
                        showGroupedRows={showGroupedRows}
                        selectedColumns={selectedColumns}
                        salaryRowTypes={salaryRowTypes}
                        workTimeReportData={data.workTimeReportData}
                        employeeGroupCalculations={
                            data.employeeGroupCalculations
                        }
                        onColumnOrderChange={onColumnOrderChange}
                        sortModel={sortModel}
                        onSortModelChange={onSortModelChange}
                    />
                )}
            </>
        </MainLayout>
    );
};
